import { useCallback, useMemo, useState } from "react";
import { useGetMyAccessScopeQuery } from "../../../store/api/accessScopeApi";
import {
  SCOPE_URL_PARAMS,
  type DepartmentFilterValue,
  type LocationFilterValue,
  type MeAccessScopeResponse,
  type ScopeFilterParams,
} from "../../../../shared/types/accessScope";
import {
  ALL,
  departmentOptions,
  readStoredScope,
  resolveSelection,
  selectionLabels,
  showDepartmentDropdown,
  toScopeParams,
  updateStoredScope,
  type ScopeDropdownOption,
} from "./scopeFilterState";
import { browserStorage, useStoredLocation } from "./locationStore";

/**
 * The page's own `useSearchParams()` result. Passed in rather than imported: the
 * app imports router hooks from both `react-router` and `react-router-dom`, and
 * under the test runner those are separate builds whose routers can't see each
 * other's hooks. Taking the page's tuple works whichever one the page uses.
 */
export type SearchParamsTuple = readonly [
  URLSearchParams,
  (next: (previous: URLSearchParams) => URLSearchParams, options?: { replace?: boolean }) => void,
];

export interface ScopeFilter {
  /**
   * False until the caller's scope has loaded. A page skips its scoped queries
   * until then, so a remembered choice never shows unfiltered rows first. A
   * failed load is ready too, with no filters: the server still enforces scope.
   */
  ready: boolean;
  scope: MeAccessScopeResponse | undefined;
  /** The page's filter row has something to show (the Department dropdown). */
  visible: boolean;
  /** The header's choice (D27), already validated. */
  location: LocationFilterValue;
  department: DepartmentFilterValue;
  showDepartment: boolean;
  departmentOptions: ScopeDropdownOption[];
  /** The chosen options' words, null for "all". For printed headers and summaries. */
  labels: { location: string | null; department: string | null };
  /** Send these to every scoped endpoint. Empty for "all". */
  params: ScopeFilterParams;
  /** Changes whenever `params` does; for resetting paging and selections. */
  paramsKey: string;
  /** `dept` for links this page builds, so navigating keeps it. Omits "all". */
  urlParams: Record<string, string>;
  setDepartment: (value: DepartmentFilterValue) => void;
}

/**
 * The header's Location choice (D27), for a page that only reads it.
 *
 * A `?loc=` in the URL counts until the header has taken it into the store and
 * removed it, so a deep link's first request is already the filtered one.
 */
export function useLocationScope(instanceId: string, searchParams?: URLSearchParams) {
  const {
    data: scope,
    isLoading,
    isError,
  } = useGetMyAccessScopeQuery(instanceId, {
    skip: !instanceId,
  });
  const stored = useStoredLocation(instanceId);
  const urlLocation = searchParams?.get(SCOPE_URL_PARAMS.location) ?? null;
  const { location } = resolveSelection({
    urlLocation,
    urlDepartment: null,
    stored: stored === null ? {} : { loc: stored },
    scope,
  });
  const params = useMemo(() => toScopeParams(location, ALL), [location]);
  return {
    ready: !!instanceId && !isLoading && (!!scope || isError),
    scope,
    location,
    params,
    paramsKey: location,
  };
}

/**
 * The header's Location choice plus this page's Department filter (D16, D27).
 *
 * For the department, the URL (`dept`) wins, so links, refresh and back keep the
 * slice. With no URL value, the last department chosen in this instance is used.
 * Anything stale falls back to "all" without a message: the controls always show
 * what is applied, so nothing is filtered invisibly.
 */
export function useScopeFilter(
  instanceId: string,
  [searchParams, setSearchParams]: SearchParamsTuple,
): ScopeFilter {
  const { ready, scope, location } = useLocationScope(instanceId, searchParams);

  const [storedDepartment, setStoredDepartment] = useState(
    () => readStoredScope(instanceId, browserStorage()).dept ?? null,
  );
  const [storedFor, setStoredFor] = useState(instanceId);
  // Switching instance re-reads that instance's remembered department.
  if (storedFor !== instanceId) {
    setStoredFor(instanceId);
    setStoredDepartment(readStoredScope(instanceId, browserStorage()).dept ?? null);
  }

  const urlDepartment = searchParams.get(SCOPE_URL_PARAMS.department);
  const { department } = resolveSelection({
    urlLocation: null,
    urlDepartment,
    stored: storedDepartment === null ? {} : { dept: storedDepartment },
    scope,
  });

  const setDepartment = useCallback(
    (value: DepartmentFilterValue) => {
      setSearchParams(
        (previous) => {
          const updated = new URLSearchParams(previous);
          if (value === ALL) updated.delete(SCOPE_URL_PARAMS.department);
          else updated.set(SCOPE_URL_PARAMS.department, value);
          return updated;
        },
        { replace: true },
      );
      setStoredDepartment(value === ALL ? null : value);
      updateStoredScope(instanceId, { dept: value }, browserStorage());
    },
    [instanceId, setSearchParams],
  );

  const params = useMemo(() => toScopeParams(location, department), [location, department]);
  const showDepartment = showDepartmentDropdown(scope);

  return {
    ready,
    scope,
    visible: ready && showDepartment,
    location,
    department,
    showDepartment,
    departmentOptions: scope ? departmentOptions(scope) : [],
    labels: selectionLabels(scope, location, department),
    params,
    paramsKey: `${location}|${department}`,
    urlParams: department !== ALL ? { [SCOPE_URL_PARAMS.department]: department } : {},
    setDepartment,
  };
}
